import loadable from "@loadable/component";
import { isLeft } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import { useMemo } from "react";
import { HtmlEmbed } from "../../../../core/content-blocks/HtmlEmbed.component";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Container } from "../../../../core/ui";
import { CoastLinkedGuests } from "../../components/CoastLinkedGuests.component";
import { getCoastShowType } from "../../lib/coastContent";
import { CoastContentBlock } from "./CoastContent.component";
import { CoastShowPayloadResolver } from "./CoastContent.types";
import { CoastContentShowBumperMusic } from "./CoastContentShowBumperMusic.component";

import type { Store } from "@inferno/renderer-shared-core";

const CoastShowAudioBoxComponent = loadable(() => import("./coast-show-audiobox/CoastShowAudioBox.component"));

const log = ILog.logger("CoastContentShow");

export type CoastContentShowProps = {
  content: Webapi.PublishRecordFragment;
  eyebrow?: Webapi.Eyebrow;
  store?: Store;
};

export const CoastContentShow = inject("store")(({ content, store }: CoastContentShowProps) => {
  if (!store) {
    return null;
  }

  const resolvedPayload = CoastShowPayloadResolver.decode(content.payload);

  if (isLeft(resolvedPayload)) {
    log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
    return null;
  }

  const { fields } = resolvedPayload.right;
  const showType = useMemo(() => getCoastShowType(content), [content]);

  return (
    <Container className="component-coast-content-show coast-content-layout-container">
      {fields.show_date?.value ? (
        <CoastShowAudioBoxComponent showType={showType} show={fields} showDate={fields.show_date?.value} />
      ) : null}
      <h3 className="about-the-show">About the show</h3>
      <article>
        {fields.blocks.value?.map((block, i) => (
          <CoastContentBlock block={block} key={i} />
        ))}
        <CoastLinkedGuests titleOnSameLine={false} item={content} guestImage={true} />
        <div className="html-embed-block">
          {fields.websites?.value ? (
            <section className="coast-content-html-block">
              <h3>Websites:</h3>
              <HtmlEmbed {...fields.websites} />
            </section>
          ) : null}

          {fields.books?.value ? (
            <section className="coast-content-html-block">
              <h3>Relevant Books:</h3>
              <HtmlEmbed {...fields.books} />
            </section>
          ) : null}

          {fields.videos?.value ? (
            <section className="coast-content-html-block">
              <h3>Videos:</h3>
              <HtmlEmbed {...fields.videos} />
            </section>
          ) : null}

          {fields.articles?.value ? (
            <section className="coast-content-html-block">
              <h3>Related Articles:</h3>
              <HtmlEmbed {...fields.articles} />
            </section>
          ) : null}
        </div>
        <CoastContentShowBumperMusic show={resolvedPayload.right} />
      </article>

      {/* TODO: Previous-next */}
    </Container>
  );
});

export default CoastContentShow;
